import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from 'react'
import {
  uploadAttachment,
  type AttachmentRow,
  type MessagingAttachmentSource,
} from '../../../api/attachments'
import { PexIcon } from '../../../components/icons/PexIcon'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'
import {
  useMessagingVoiceRecorder,
  type VoiceRecorderErrorReason,
} from './useMessagingVoiceRecorder'
import { VoiceMessagePlayer } from './VoiceMessagePlayer'

type MessagingVoiceRecorderProps = {
  appearance: 'default' | 'messenger' | 'admin-desk'
  source: MessagingAttachmentSource
  visible?: boolean
  disabled: boolean
  hasOtherAttachments: boolean
  selectedVoice?: AttachmentRow
  onUploaded: (row: AttachmentRow) => void
  onRemoveConfirmed: (id: string) => void
  onBusyChange: (busy: boolean) => void
  onControlStateChange?: (state: VoiceRecorderControlState) => void
  onSendVoice: (row: AttachmentRow) => Promise<void>
}

export type VoiceRecorderControlState = {
  phase:
    | 'idle'
    | 'requesting'
    | 'recording'
    | 'preview'
    | 'uploading'
    | 'failed'
    | 'error'
  canSubmit: boolean
  canActivate: boolean
  errorReason?: VoiceRecorderErrorReason
  elapsedSeconds: number
}

export type MessagingVoiceRecorderHandle = {
  activate: () => void
  discard: () => void
  submitPreview: () => Promise<boolean>
}

type UploadState = 'idle' | 'uploading' | 'failed' | 'uploaded'

const IDLE_UPLOAD_STATE: UploadState = 'idle'

function formatElapsed(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`
}

export const MessagingVoiceRecorder = forwardRef<
  MessagingVoiceRecorderHandle,
  MessagingVoiceRecorderProps
>(function MessagingVoiceRecorder(
  {
    appearance,
    source,
    visible = true,
    disabled,
    hasOtherAttachments,
    selectedVoice,
    onUploaded,
    onRemoveConfirmed,
    onBusyChange,
    onControlStateChange,
    onSendVoice,
  },
  ref,
) {
  const t = useT(messagingI18n)
  const { component, objectId, refetch } = source
  const [uploadSnapshot, setUploadSnapshot] = useState<{
    key: string
    state: UploadState
  }>(() => ({ key: objectId, state: IDLE_UPLOAD_STATE }))
  const uploadState =
    uploadSnapshot.key === objectId
      ? uploadSnapshot.state
      : IDLE_UPLOAD_STATE
  const uploadGenerationRef = useRef(0)
  const mountedRef = useRef(true)
  const onBusyChangeRef = useRef(onBusyChange)
  const onUploadedRef = useRef(onUploaded)
  const onSendVoiceRef = useRef(onSendVoice)
  const refetchRef = useRef(refetch)
  const confirmedRowRef = useRef<AttachmentRow | null>(selectedVoice ?? null)
  const submissionRef = useRef<Promise<boolean> | null>(null)
  const deliverySucceededRef = useRef(false)

  const setUploadState = useCallback(
    (state: UploadState) => {
      setUploadSnapshot({ key: objectId, state })
    },
    [objectId],
  )

  useEffect(() => {
    onBusyChangeRef.current = onBusyChange
    onUploadedRef.current = onUploaded
    onSendVoiceRef.current = onSendVoice
    refetchRef.current = refetch
  }, [onBusyChange, onSendVoice, onUploaded, refetch])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      uploadGenerationRef.current += 1
      onBusyChangeRef.current(false)
    }
  }, [])

  useEffect(() => {
    confirmedRowRef.current = null
    submissionRef.current = null
    deliverySucceededRef.current = false
    return () => {
      uploadGenerationRef.current += 1
    }
  }, [objectId])

  useEffect(() => {
    if (selectedVoice) confirmedRowRef.current = selectedVoice
  }, [objectId, selectedVoice])

  const uploadVoice = useCallback(
    async (file: File) => {
      const generation = uploadGenerationRef.current + 1
      uploadGenerationRef.current = generation
      setUploadState('uploading')
      let row: AttachmentRow
      try {
        // Drive's persisted role contract deliberately stays generic; Messaging
        // derives voice semantics from the server-verified audio content type.
        // Passing a synthetic `voice` role makes confirm fail at request parsing.
        row = await uploadAttachment(component, objectId, file)
      } catch {
        if (mountedRef.current && uploadGenerationRef.current === generation) {
          setUploadState('failed')
        }
        return null
      }
      if (!mountedRef.current || uploadGenerationRef.current !== generation) {
        return null
      }
      confirmedRowRef.current = row
      onUploadedRef.current(row)
      setUploadState('uploaded')
      try {
        await refetchRef.current()
      } catch {
        // The confirmed Drive row remains selected even if list refresh fails.
      }
      return row
    },
    [component, objectId, setUploadState],
  )

  const recorder = useMessagingVoiceRecorder(objectId)
  const preview = recorder.state.status === 'preview' ? recorder.state : null
  const busy =
    recorder.state.status === 'requesting' ||
    recorder.state.status === 'recording' ||
    uploadState === 'uploading'

  useEffect(() => {
    onBusyChange(busy)
  }, [busy, onBusyChange])

  const removeVoice = useCallback(() => {
    uploadGenerationRef.current += 1
    if (selectedVoice) onRemoveConfirmed(selectedVoice.id)
    recorder.cancel()
    confirmedRowRef.current = null
    submissionRef.current = null
    deliverySucceededRef.current = false
    setUploadState('idle')
  }, [onRemoveConfirmed, recorder, selectedVoice, setUploadState])

  const submitPreview = useCallback((): Promise<boolean> => {
    if (submissionRef.current) return submissionRef.current
    if (deliverySucceededRef.current) return Promise.resolve(true)
    if (!preview || disabled || hasOtherAttachments) {
      return Promise.resolve(false)
    }

    const operation = (async () => {
      const row = confirmedRowRef.current ?? (await uploadVoice(preview.file))
      if (!row) return false

      setUploadState('uploading')
      try {
        await onSendVoiceRef.current(row)
        if (!mountedRef.current) return false
        deliverySucceededRef.current = true
        recorder.cancel()
        setUploadState('uploaded')
        return true
      } catch {
        if (mountedRef.current) setUploadState('failed')
        return false
      }
    })()

    submissionRef.current = operation
    void operation.finally(() => {
      if (submissionRef.current === operation) submissionRef.current = null
    })
    return operation
  }, [disabled, hasOtherAttachments, preview, recorder, setUploadState, uploadVoice])

  useImperativeHandle(
    ref,
    () => ({
      activate() {
        if (disabled || hasOtherAttachments || selectedVoice) return
        if (
          recorder.state.status === 'idle' ||
          (recorder.state.status === 'error' &&
            recorder.state.reason !== 'unsupported')
        ) {
          void recorder.start()
        } else if (recorder.state.status === 'requesting') {
          recorder.cancel()
        } else if (recorder.state.status === 'recording') {
          recorder.stop()
        }
      },
      discard: removeVoice,
      submitPreview,
    }),
    [
      disabled,
      hasOtherAttachments,
      recorder,
      removeVoice,
      selectedVoice,
      submitPreview,
    ],
  )

  const retryableRecorder =
    recorder.state.status === 'error' &&
    recorder.state.reason !== 'unsupported'
  const canActivate =
    !disabled &&
    !hasOtherAttachments &&
    !selectedVoice &&
    (recorder.state.status === 'idle' ||
      recorder.state.status === 'requesting' ||
      recorder.state.status === 'recording' ||
      retryableRecorder)
  const canSubmit =
    Boolean(preview) &&
    !disabled &&
    !hasOtherAttachments &&
    uploadState !== 'uploading'
  const phase: VoiceRecorderControlState['phase'] =
    uploadState === 'uploading'
      ? 'uploading'
      : uploadState === 'failed'
        ? 'failed'
        : recorder.state.status
  const elapsedSeconds =
    recorder.state.status === 'recording' || recorder.state.status === 'preview'
      ? recorder.state.elapsedSeconds
      : 0
  const errorReason =
    recorder.state.status === 'error' ? recorder.state.reason : undefined
  const controlState = useMemo<VoiceRecorderControlState>(
    () => ({
      phase,
      canSubmit,
      canActivate,
      ...(errorReason ? { errorReason } : {}),
      elapsedSeconds,
    }),
    [canActivate, canSubmit, elapsedSeconds, errorReason, phase],
  )

  useEffect(() => {
    onControlStateChange?.(controlState)
  }, [controlState, onControlStateChange])

  const panelClass =
    appearance === 'messenger'
      ? 'rounded-xl border border-white/10 bg-white/[0.04] p-2.5 text-slate-100'
      : 'rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-2.5 text-[var(--color-text)]'
  const actionClass =
    'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-current/15 px-3 text-xs font-semibold transition hover:bg-current/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-40'
  const discardIconClass =
    'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-current/15 text-[var(--color-text-muted)] transition hover:bg-current/[0.06] hover:text-[var(--color-danger)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-40'

  const errorText: Record<VoiceRecorderErrorReason, string> = {
    permission_denied: t.media.microphonePermissionDenied,
    unsupported: t.media.voiceUnsupported,
    interrupted: t.media.voiceInterrupted,
  }

  let content: ComponentProps<'div'>['children'] = null
  if (recorder.state.status === 'requesting') {
    content = (
      <span className="text-xs font-medium" role="status">
        {t.media.requestingMicrophone}
      </span>
    )
  } else if (recorder.state.status === 'recording') {
    content = (
      <div className="flex flex-wrap items-center gap-2">
        <span className="sr-only" role="status">
          {t.media.recording}
        </span>
        <span
          className="font-mono text-xs tabular-nums"
          role="timer"
          aria-live="off"
        >
          {t.media.recordingElapsed(formatElapsed(recorder.state.elapsedSeconds))}
        </span>
        <span
          data-testid="voice-level"
          aria-hidden="true"
          className="h-2 w-20 overflow-hidden rounded-full bg-current/15"
        >
          <span
            className="block h-full origin-left rounded-full bg-[var(--color-action)] transition-transform motion-reduce:transition-none"
            style={{ transform: `scaleX(${recorder.state.level})` }}
          />
        </span>
        <button type="button" onClick={removeVoice} className={actionClass}>
          <PexIcon name="close" size="xs" decorative />
          {t.media.cancelRecording}
        </button>
      </div>
    )
  } else if (recorder.state.status === 'error') {
    content = (
      <p className="text-xs font-medium text-[var(--color-danger)]" role="alert">
        {errorText[recorder.state.reason]}
      </p>
    )
  } else if (preview) {
    content = (
      <div className="space-y-2" aria-label={t.media.voicePreview}>
        <div className="flex min-w-0 items-center gap-2">
          <VoiceMessagePlayer
            src={preview.previewUrl}
            label={t.media.voicePreview}
            appearance={appearance === 'messenger' ? 'messenger' : 'default'}
            durationHintSeconds={preview.elapsedSeconds}
          />
          <button
            type="button"
            disabled={disabled || uploadState === 'uploading'}
            onClick={removeVoice}
            aria-label={t.media.removeVoiceMessage}
            title={t.media.removeVoiceMessage}
            className={discardIconClass}
          >
            <PexIcon name="trash" size="sm" decorative />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {uploadState === 'uploading' ? (
            <span className="text-xs" role="status">
              {t.media.uploading}
            </span>
          ) : null}
          {uploadState === 'failed' ? (
            <span className="flex items-center gap-2">
              <span className="sr-only" role="alert">
                {t.media.uploadFailed}
              </span>
              <button
                type="button"
                onClick={() => void submitPreview()}
                className={actionClass}
              >
                <PexIcon name="refresh" size="xs" decorative />
                {t.media.retryUpload}
              </button>
            </span>
          ) : null}
          {preview.elapsedSeconds >= 120 ? (
            <span className="text-xs text-[var(--color-text-muted)]">
              {t.media.recordingLimitReached}
            </span>
          ) : null}
        </div>
      </div>
    )
  } else if (selectedVoice) {
    content = (
      <div
        className="flex flex-wrap items-center gap-2"
        aria-label={t.media.voicePreview}
      >
        {selectedVoice.download_url ? (
          <VoiceMessagePlayer
            src={selectedVoice.download_url}
            label={t.media.voicePreview}
            appearance={appearance === 'messenger' ? 'messenger' : 'default'}
          />
        ) : (
          <span className="text-xs">{t.media.voiceAttachmentLabel}</span>
        )}
        <button
          type="button"
          disabled={disabled}
          onClick={removeVoice}
          aria-label={t.media.removeVoiceMessage}
          title={t.media.removeVoiceMessage}
          className={discardIconClass}
        >
          <PexIcon name="trash" size="sm" decorative />
        </button>
      </div>
    )
  }

  return (
    <section
      className={panelClass}
      data-appearance={appearance}
      hidden={!visible}
    >
      {content}
    </section>
  )
})
